<!-- components/base/components/DraggableResizableBox.vue -->
<template>
  <div
    class="draggable-box"
    ref="boxRef"
    :class="[boxClasses, { active: isActive, readonly: props.readonly }]"
    :style="boxStyle"
    @mousedown="startDrag"
  >
    <!-- Cabeçalho da caixa (opcional) -->
    <div v-if="props.showHeader" class="box-header" :class="headerClasses">
      <div class="box-title">{{ props.title }}</div>
      <div class="box-actions">
        <button
          v-if="props.allowClose"
          @click.stop="onClose"
          class="box-close-btn"
        >
          ×
        </button>
      </div>
    </div>

    <!-- Conteúdo encapsulado -->
    <div class="box-content">
      <slot></slot>
    </div>

    <!-- Alças de redimensionamento -->
    <div
      v-if="isActive && !props.readonly"
      v-for="handle in resizeHandles"
      :key="handle.position"
      class="resize-handle"
      :class="[handle.class, handleClasses]"
      @mousedown.stop="(e) => startResize(e, handle.position)"
    ></div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  inject,
  watch,
  onMounted,
  onBeforeUnmount,
  type Ref,
} from "vue";
import { throttle } from "lodash";

// Definições de tipos
interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface BoxRegistration {
  id: string;
  position: Position;
  size: Size;
}

interface ResizeHandle {
  position: string;
  class: string;
}

interface BoxProps {
  id: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  initialX?: number;
  initialY?: number;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
  title?: string;
  showHeader?: boolean;
  allowClose?: boolean;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundGradient?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: string;
  borderRadius?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  handleColor?: string;
  handleBorderColor?: string;
  active?: boolean;
  readonly?: boolean;
}

// Configuração de debug
const DEBUG = false;
const debugLog = (...args: any[]) => {
  if (DEBUG) console.log(...args);
};

// Definir props com tipagem
const props = withDefaults(defineProps<BoxProps>(), {
  id: "",
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  initialX: 0,
  initialY: 0,
  initialWidth: 20,
  initialHeight: 20,
  minWidth: 10,
  minHeight: 10,
  title: "",
  showHeader: false,
  allowClose: false,
  backgroundColor: "mdw-white",
  backgroundImage: "",
  backgroundGradient: "",
  borderColor: "mdw-gray-200",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: "mdw-rounded-md",
  headerBackgroundColor: "mdw-bg-gray-50",
  headerTextColor: "mdw-text-gray-800",
  handleColor: "mdw-bg-white",
  handleBorderColor: "mdw-border-gray-300",
  active: false,
  readonly: false,
});

// Definir eventos emitidos
const emit = defineEmits([
  "update:position",
  "update:size",
  "update:active",
  "update:x",
  "update:y",
  "update:w",
  "update:h",
  "close",
]);

// Injeções do container
const containerSize = inject<Ref<{ width: number; height: number }>>(
  "containerSize",
  ref({ width: 0, height: 0 }),
);
const registerBox = inject<(box: BoxRegistration) => void>(
  "registerBox",
  () => {},
);
const updateBox = inject<(id: string, position: Position, size: Size) => void>(
  "updateBox",
  () => {},
);
const removeBox = inject<(id: string) => void>("removeBox", () => {});

// Refs e estado
const boxRef = ref<HTMLElement | null>(null);
const position = ref<Position>({
  x: props.x !== undefined ? props.x : props.initialX,
  y: props.y !== undefined ? props.y : props.initialY,
});
const size = ref<Size>({
  width: props.w !== undefined ? props.w : props.initialWidth,
  height: props.h !== undefined ? props.h : props.initialHeight,
});

// Estado da interação
const isActive = ref(props.active);
const isDragging = ref(false);
const isResizing = ref(false);
const resizeDirection = ref<string | null>(null);
const startPos = ref({ x: 0, y: 0 });
const startSize = ref({ width: 0, height: 0 });
const startBoxPos = ref({ x: 0, y: 0 });
const boxStyleCache = ref<Record<string, string> | null>(null);

// Alças de redimensionamento
const resizeHandles: ResizeHandle[] = [
  { position: "n", class: "n" },
  { position: "s", class: "s" },
  { position: "e", class: "e" },
  { position: "w", class: "w" },
  { position: "ne", class: "ne" },
  { position: "nw", class: "nw" },
  { position: "se", class: "se" },
  { position: "sw", class: "sw" },
];

// Classes computadas para a caixa - memoizado para performance
const boxClasses = computed(() => {
  const classes = [];

  if (props.backgroundColor.startsWith("mdw-")) {
    classes.push(props.backgroundColor);
  }

  if (props.backgroundGradient) {
    classes.push(`mdw-bg-gradient-${props.backgroundGradient}`);
  }

  if (props.borderStyle !== "none") {
    classes.push(`mdw-border-${props.borderStyle}`);
    if (props.borderColor.startsWith("mdw-")) {
      classes.push(props.borderColor);
    }
  }

  if (props.borderRadius) {
    classes.push(props.borderRadius);
  }

  return classes.join(" ");
});

// Classes para o cabeçalho
const headerClasses = computed(() => {
  return [props.headerBackgroundColor, props.headerTextColor].join(" ");
});

// Classes para as alças de redimensionamento
const handleClasses = computed(() => {
  return [props.handleColor, props.handleBorderColor].join(" ");
});

// Estilo computado da caixa - otimizado
const boxStyle = computed(() => {
  // Usar cache se não estiver em modo interativo
  if (!isDragging.value && !isResizing.value && boxStyleCache.value) {
    return boxStyleCache.value;
  }

  const style: Record<string, string> = {
    left: `${position.value.x}%`,
    top: `${position.value.y}%`,
    width: `${size.value.width}%`,
    height: `${size.value.height}%`,
    zIndex: isResizing.value || isDragging.value || isActive.value ? "10" : "1",
  };

  if (props.backgroundColor && !props.backgroundColor.startsWith("mdw-")) {
    style["backgroundColor"] = props.backgroundColor;
  }

  if (props.backgroundImage) {
    style["backgroundImage"] = `url('${props.backgroundImage}')`;
    style["backgroundSize"] = "cover";
    style["backgroundPosition"] = "center";
  }

  if (props.borderStyle !== "none") {
    style["borderWidth"] = `${props.borderWidth}px`;
    if (props.borderColor && !props.borderColor.startsWith("mdw-")) {
      style["borderColor"] = props.borderColor;
    }
  }

  // Cache o resultado se não estiver em modo interativo
  if (!isDragging.value && !isResizing.value) {
    boxStyleCache.value = style;
  }

  return style;
});

// Watches otimizados para sincronizar props e estado interno
watch(
  () => position.value,
  (newPosition) => {
    emit("update:x", newPosition.x);
    emit("update:y", newPosition.y);
    emit("update:position", newPosition);
    boxStyleCache.value = null;
  },
  { deep: true },
);

watch(
  () => size.value,
  (newSize) => {
    emit("update:w", newSize.width);
    emit("update:h", newSize.height);
    emit("update:size", newSize);
    boxStyleCache.value = null;
  },
  { deep: true },
);

// Watches para props externas (x, y, w, h)
watch(
  () => [props.x, props.y],
  ([newX, newY]) => {
    if (isDragging.value) return;

    if (newX !== undefined && position.value.x !== newX) {
      position.value.x = newX;
    }

    if (newY !== undefined && position.value.y !== newY) {
      position.value.y = newY;
    }
  },
);

watch(
  () => [props.w, props.h],
  ([newW, newH]) => {
    if (isResizing.value) return;

    if (newW !== undefined && size.value.width !== newW) {
      size.value.width = newW;
    }

    if (newH !== undefined && size.value.height !== newH) {
      size.value.height = newH;
    }
  },
);

// Watch para prop active
watch(
  () => props.active,
  (newVal) => {
    isActive.value = newVal;
  },
);

// Funções para arrastar - otimizadas com throttle e requestAnimationFrame
function startDrag(event: MouseEvent) {
  if (props.readonly) return;

  // Ignorar se o clique foi em um elemento de controle
  if (
    event.target instanceof HTMLElement &&
    (event.target.classList.contains("resize-handle") ||
      event.target.classList.contains("box-close-btn"))
  ) {
    return;
  }

  // Ativar a caixa
  isActive.value = true;
  emit("update:active", true);

  debugLog("Iniciando arrasto");

  isDragging.value = true;
  startPos.value = {
    x: event.clientX,
    y: event.clientY,
  };
  startBoxPos.value = {
    x: position.value.x,
    y: position.value.y,
  };

  document.addEventListener("mousemove", throttledDrag);
  document.addEventListener("mouseup", stopDrag);

  // Prevenir comportamento padrão
  event.preventDefault();
}

// Função de arrastar com throttle para melhor desempenho
const throttledDrag = throttle((event: MouseEvent) => {
  if (!isDragging.value) return;

  const deltaX = event.clientX - startPos.value.x;
  const deltaY = event.clientY - startPos.value.y;

  // Obter o tamanho do container
  let containerWidth = containerSize.value.width;
  let containerHeight = containerSize.value.height;

  // Fallback se dimensões não disponíveis
  if (containerWidth <= 0 || containerHeight <= 0) {
    const containerElement = document.querySelector(".draggable-container");
    containerWidth = containerElement?.clientWidth || window.innerWidth;
    containerHeight = containerElement?.clientHeight || window.innerHeight;
  }

  // Converter delta para porcentagem
  const deltaXPercent = (deltaX / containerWidth) * 100;
  const deltaYPercent = (deltaY / containerHeight) * 100;

  // Calcular nova posição respeitando limites
  let newX = startBoxPos.value.x + deltaXPercent;
  let newY = startBoxPos.value.y + deltaYPercent;

  // Limites
  newX = Math.max(0, Math.min(100 - size.value.width, newX));
  newY = Math.max(0, Math.min(100 - size.value.height, newY));

  // Atualizar posição usando requestAnimationFrame para suavizar
  requestAnimationFrame(() => {
    position.value = { x: newX, y: newY };
  });
}, 16); // ~60fps

function stopDrag() {
  debugLog("Arrasto finalizado");
  isDragging.value = false;

  // Atualizar o container uma única vez ao finalizar
  updateBox(props.id, position.value, size.value);

  document.removeEventListener("mousemove", throttledDrag);
  document.removeEventListener("mouseup", stopDrag);
}

// Funções para redimensionar - otimizadas
function startResize(event: MouseEvent, direction: string) {
  if (props.readonly) return;

  debugLog("Iniciando redimensionamento", direction);

  isActive.value = true;
  emit("update:active", true);

  isResizing.value = true;
  resizeDirection.value = direction;
  startPos.value = {
    x: event.clientX,
    y: event.clientY,
  };
  startSize.value = {
    width: size.value.width,
    height: size.value.height,
  };
  startBoxPos.value = {
    x: position.value.x,
    y: position.value.y,
  };

  document.addEventListener("mousemove", throttledResize);
  document.addEventListener("mouseup", stopResize);

  event.preventDefault();
  event.stopPropagation();
}

// Função de redimensionamento com throttle
const throttledResize = throttle((event: MouseEvent) => {
  if (!isResizing.value || !resizeDirection.value) return;

  const deltaX = event.clientX - startPos.value.x;
  const deltaY = event.clientY - startPos.value.y;

  // Obter dimensões do container
  let containerWidth = containerSize.value.width;
  let containerHeight = containerSize.value.height;

  if (containerWidth <= 0 || containerHeight <= 0) {
    const containerElement = document.querySelector(".draggable-container");
    containerWidth = containerElement?.clientWidth || window.innerWidth;
    containerHeight = containerElement?.clientHeight || window.innerHeight;
  }

  // Converter delta para porcentagem
  const deltaXPercent = (deltaX / containerWidth) * 100;
  const deltaYPercent = (deltaY / containerHeight) * 100;

  // Processar redimensionamento em requestAnimationFrame
  requestAnimationFrame(() => {
    processResize(deltaXPercent, deltaYPercent);
  });
}, 16);

function processResize(deltaXPercent: number, deltaYPercent: number) {
  const newSize = { ...size.value };
  const newPosition = { ...position.value };

  // Redimensionar de acordo com a direção
  switch (resizeDirection.value) {
    case "e":
      newSize.width = Math.max(
        props.minWidth,
        startSize.value.width + deltaXPercent,
      );
      newSize.width = Math.min(100 - newPosition.x, newSize.width);
      break;
    case "w":
      const widthChange = deltaXPercent;
      const newWidth = startSize.value.width - widthChange;

      if (newWidth >= props.minWidth) {
        const newPosX = startBoxPos.value.x + widthChange;
        if (newPosX >= 0) {
          newSize.width = newWidth;
          newPosition.x = newPosX;
        }
      }
      break;
    case "s":
      newSize.height = Math.max(
        props.minHeight,
        startSize.value.height + deltaYPercent,
      );
      newSize.height = Math.min(100 - newPosition.y, newSize.height);
      break;
    case "n":
      const heightChange = deltaYPercent;
      const newHeight = startSize.value.height - heightChange;

      if (newHeight >= props.minHeight) {
        const newPosY = startBoxPos.value.y + heightChange;
        if (newPosY >= 0) {
          newSize.height = newHeight;
          newPosition.y = newPosY;
        }
      }
      break;
    case "se":
      newSize.width = Math.max(
        props.minWidth,
        startSize.value.width + deltaXPercent,
      );
      newSize.height = Math.max(
        props.minHeight,
        startSize.value.height + deltaYPercent,
      );
      newSize.width = Math.min(100 - newPosition.x, newSize.width);
      newSize.height = Math.min(100 - newPosition.y, newSize.height);
      break;
    case "sw":
      // Altura (como "s")
      newSize.height = Math.max(
        props.minHeight,
        startSize.value.height + deltaYPercent,
      );
      newSize.height = Math.min(100 - newPosition.y, newSize.height);

      // Largura e posição x (como "w")
      const swWidthChange = deltaXPercent;
      const swNewWidth = startSize.value.width - swWidthChange;
      if (swNewWidth >= props.minWidth) {
        const swNewPosX = startBoxPos.value.x + swWidthChange;
        if (swNewPosX >= 0) {
          newSize.width = swNewWidth;
          newPosition.x = swNewPosX;
        }
      }
      break;
    case "ne":
      // Largura (como "e")
      newSize.width = Math.max(
        props.minWidth,
        startSize.value.width + deltaXPercent,
      );
      newSize.width = Math.min(100 - newPosition.x, newSize.width);

      // Altura e posição y (como "n")
      const neHeightChange = deltaYPercent;
      const neNewHeight = startSize.value.height - neHeightChange;
      if (neNewHeight >= props.minHeight) {
        const neNewPosY = startBoxPos.value.y + neHeightChange;
        if (neNewPosY >= 0) {
          newSize.height = neNewHeight;
          newPosition.y = neNewPosY;
        }
      }
      break;
    case "nw":
      // Largura (x)
      const nwWidthChange = deltaXPercent;
      const nwNewWidth = startSize.value.width - nwWidthChange;
      if (nwNewWidth >= props.minWidth) {
        const nwNewPosX = startBoxPos.value.x + nwWidthChange;
        if (nwNewPosX >= 0) {
          newSize.width = nwNewWidth;
          newPosition.x = nwNewPosX;
        }
      }

      // Altura (y)
      const nwHeightChange = deltaYPercent;
      const nwNewHeight = startSize.value.height - nwHeightChange;
      if (nwNewHeight >= props.minHeight) {
        const nwNewPosY = startBoxPos.value.y + nwHeightChange;
        if (nwNewPosY >= 0) {
          newSize.height = nwNewHeight;
          newPosition.y = nwNewPosY;
        }
      }
      break;
  }

  // Garantir limites
  newSize.width = Math.max(
    props.minWidth,
    Math.min(100 - newPosition.x, newSize.width),
  );
  newSize.height = Math.max(
    props.minHeight,
    Math.min(100 - newPosition.y, newSize.height),
  );

  // Atualizar estado
  size.value = newSize;
  position.value = newPosition;
}

function stopResize() {
  debugLog("Redimensionamento finalizado");
  isResizing.value = false;

  // Atualizar o container uma única vez ao finalizar
  updateBox(props.id, position.value, size.value);

  document.removeEventListener("mousemove", throttledResize);
  document.removeEventListener("mouseup", stopResize);
}

// Fechamento de caixa
function onClose() {
  removeBox(props.id);
  emit("close", props.id);
}

// Ciclo de vida do componente
onMounted(() => {
  debugLog("Box montada com ID:", props.id);

  // Priorizar os valores de x, y, w, h se estiverem definidos
  if (props.x !== undefined) position.value.x = props.x;
  if (props.y !== undefined) position.value.y = props.y;
  if (props.w !== undefined) size.value.width = props.w;
  if (props.h !== undefined) size.value.height = props.h;

  // Registrar box
  registerBox({
    id: props.id,
    position: position.value,
    size: size.value,
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", throttledDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("mousemove", throttledResize);
  document.removeEventListener("mouseup", stopResize);
});
</script>

<style scoped>
.draggable-box {
  position: absolute;
  box-sizing: border-box;
  cursor: move;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  transition:
    box-shadow 160ms ease,
    outline-color 160ms ease,
    transform 160ms ease;
}
/* Adicione este estilo para cursor quando em modo somente leitura */
.draggable-box.readonly {
  cursor: default !important;
}
.draggable-box.readonly .resize-handle {
  display: none; /* Oculta as alças de redimensionamento no modo somente leitura */
}
.draggable-box.active {
  z-index: 10;
  outline: 2px solid #194162;
  outline-offset: 1px;
  box-shadow: 0 8px 22px rgba(25, 65, 98, 0.18);
}
.draggable-box.readonly .resize-handle {
  display: none; /* Oculta as alças de redimensionamento no modo somente leitura */
}
.box-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  cursor: move;
}

.box-title {
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.box-content {
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: clamp(4px, 0.6vw, 10px);
}

.box-close-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  margin-left: 8px;
  border-radius: 3px;
}

.box-close-btn:hover {
  background-color: #e2e8f0;
  color: #64748b;
}

.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: #194162;
  border-width: 1px;
  border-style: solid;
  border-color: #194162;
  z-index: 2;
  opacity: 0;
  transition: opacity 120ms ease;
}

.draggable-box:hover .resize-handle,
.draggable-box.active .resize-handle {
  opacity: 1;
}

/* Posicionamento das alças */
.n {
  top: -5px;
  left: 50%;
  border-radius: 0 0 2px 2px;
  transform: translateX(-50%);
  cursor: n-resize;
}

.s {
  bottom: -5px;
  left: 50%;
  border-radius: 2px 2px 0 0;
  transform: translateX(-50%);
  cursor: s-resize;
}

.e {
  top: 50%;
  right: -5px;
  border-radius: 2px 0 0 2px;
  transform: translateY(-50%);
  cursor: e-resize;
}

.w {
  top: 50%;
  left: -5px;
  border-radius: 0 2px 2px 0;
  transform: translateY(-50%);
  cursor: w-resize;
}

.ne {
  width: 10px;
  height: 10px;
  border-radius: 0 0 2px 2px;
  top: -5px;
  right: -5px;
  cursor: ne-resize;
}

.nw {
  width: 10px;
  height: 10px;
  border-radius: 0 0 2px 0;
  top: -5px;
  left: -5px;
  cursor: nw-resize;
}

.se {
  width: 10px;
  height: 10px;
  border-radius: 2px 0 0 0;
  bottom: -5px;
  right: -5px;
  cursor: se-resize;
}

.sw {
  width: 10px;
  height: 10px;
  border-radius: 0 2px 0 0;
  bottom: -5px;
  left: -5px;
  cursor: sw-resize;
}
</style>
